import {
	AppBar,
	Avatar, Backdrop,
	Box,
	Button, CircularProgress,
	Container,
	IconButton,
	Menu,
	MenuItem, Stack,
	Toolbar,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { FC, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AdbIcon from '@mui/icons-material/Adb';
import { useTranslation } from '../../services';

import { useAuth0 } from '@auth0/auth0-react';
import {useCheckRole} from "../../utils";

export interface AdminInterface {}

const Admin: FC<AdminInterface> = () => {
	const { error, user, isAuthenticated, loginWithRedirect, logout, isLoading } =
		useAuth0();

	const {accessToAdmin} = useCheckRole();
	const logoutWithRedirect = () =>
		logout({
			returnTo: `${window.location.origin}/admin`,
		});

	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const pages = [
		{ title: t('Study Request Management'), path: '/admin' },
		{ title: t('Students Management'), path: '/admin/students' },
		{
			title: t('New Study Request'),
			path: '/admin/study-request',
		},
		{ title: t('History of changes'), path: '/admin/history' },
		{ title: t('Request Basic Registration Access'), path: '/admin/open-basic-access' },
	];

	const settings = [{ title: t('Logout'), onClick: logoutWithRedirect }];
	const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
	const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

	const handleOpenNavMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			setAnchorElNav(event.currentTarget);
		},
		[setAnchorElNav]
	);
	const handleOpenUserMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			setAnchorElUser(event.currentTarget);
		},
		[setAnchorElUser]
	);

	const handleCloseNavMenu = useCallback(
		(page: { title: string; path: string }) => {
			navigate(page.path);
			setAnchorElNav(null);
		},
		[navigate, setAnchorElNav]
	);

	const handleCloseUserMenu = useCallback(() => {
		setAnchorElUser(null);
	}, [setAnchorElUser]);

	if (isLoading) {
		return <Backdrop
			sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
			open={isLoading}
		>
			<CircularProgress color="inherit" sx={{ m: 1 }} />
			<Stack direction="row" justifyContent="center">
				{t('Please wait...')}
			</Stack>
		</Backdrop>;
	}

	if (error) {
		return (
			<div>
				{error.message}
				<Button onClick={() => logoutWithRedirect()}>{t('Logout')}</Button>
			</div>
		);
	}

	if (!isAuthenticated && !error) {
		loginWithRedirect();
	}

	return accessToAdmin ? (
					<>
						<AppBar position="static" color="transparent">
							<Container maxWidth="xl">
								<Toolbar disableGutters>
									<Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
										<IconButton
											size="large"
											aria-label="account of current user"
											aria-controls="menu-appbar"
											aria-haspopup="true"
											onClick={handleOpenNavMenu}
											color="inherit"
										>
											<MenuIcon />
										</IconButton>
										<Menu
											id="menu-appbar"
											anchorEl={anchorElNav}
											anchorOrigin={{
												vertical: 'bottom',
												horizontal: 'left',
											}}
											keepMounted
											transformOrigin={{
												vertical: 'top',
												horizontal: 'left',
											}}
											open={Boolean(anchorElNav)}
											onClose={handleCloseNavMenu}
											sx={{
												display: { xs: 'block', md: 'none' },
											}}
										>
											{pages.map((page, i) => (
												<MenuItem
													key={`link_${i}`}
													onClick={() => handleCloseNavMenu(page)}
												>
													<Typography textAlign="center">
														{page.title}
													</Typography>
												</MenuItem>
											))}
										</Menu>
									</Box>
									<Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
										{pages.map((page, i) => (
											<Button
												key={`link_${i}`}
												onClick={() => handleCloseNavMenu(page)}
												sx={{ display: 'block', mr: 3 }}
												variant="text"
												color={
													location.pathname === page.path
														? 'success'
														: 'primary'
												}
											>
												{page.title}
											</Button>
										))}
									</Box>

									<Box sx={{ flexGrow: 0 }}>
										<Tooltip title="Open settings">
											<IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
												<Avatar alt={user?.name} src={user?.picture} />
											</IconButton>
										</Tooltip>
										<Menu
											sx={{ mt: '45px' }}
											id="menu-appbar"
											anchorEl={anchorElUser}
											anchorOrigin={{
												vertical: 'top',
												horizontal: 'right',
											}}
											keepMounted
											transformOrigin={{
												vertical: 'top',
												horizontal: 'right',
											}}
											open={Boolean(anchorElUser)}
											onClose={handleCloseUserMenu}
										>
											{settings.map((setting) => (
												<MenuItem
													key={setting.title}
													onClick={setting.onClick || handleCloseUserMenu}
												>
													<Typography textAlign="center">
														{setting.title}
													</Typography>
												</MenuItem>
											))}
										</Menu>
									</Box>
								</Toolbar>
							</Container>
						</AppBar>
						<Outlet></Outlet>
					</>
				): <div>
					{t('ACCESS DENIED')}
					<Button onClick={() => logoutWithRedirect()}>{t('Logout')}</Button>
				</div>
};

export default Admin;
